import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import ViewLead from "@/features/leads/components/view-lead/ViewLead";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import { useLeadSheetMount } from "@/features/leads/hooks/useLeadSheetMount";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { takePendingChild } from "./openLead";

/**
 * The lead screen on a phone. Every "open this lead" in the app — a card, a
 * follow-up row, a notification, a chat — dispatches `setCurrentLeadId`; on
 * the desktop only the leads page listens, here the layout does, so a lead
 * opens over whichever tab the person is on (the app's `navigateToLeadDetails`).
 *
 * The phone's Back button closes it: opening pushes a history entry marked
 * with the lead id, Back pops it and the POP below clears the lead (or
 * restores the one underneath when a lead was opened from a lead).
 */
export default function MobileLeadHost() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const currentLeadId = useAppSelector((s) => s.leadFunctional.currentLeadId);
  const active = currentLeadId != null ? String(currentLeadId) : null;
  const mounted = useLeadSheetMount(active);
  const pushedFor = useRef<string | null>(null);
  // The deal a row asked to open on (openLead), read once per lead.
  const [childFor, setChildFor] = useState<{ lead: string; child: number | null } | null>(null);
  useEffect(() => {
    if (active) setChildFor({ lead: active, child: takePendingChild() });
  }, [active]);
  const defaultChildId = childFor && childFor.lead === mounted ? (childFor.child ?? undefined) : undefined;

  // A lead opened → one history entry carrying its id.
  useEffect(() => {
    if (!active || pushedFor.current === active) return;
    pushedFor.current = active;
    const state = (location.state ?? {}) as Record<string, unknown>;
    if (state.xmLead === active) return;
    navigate({ pathname: location.pathname, search: location.search }, { state: { ...state, xmLead: active } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Back (POP) → the lead the entry underneath names, or none.
  useEffect(() => {
    if (navType !== "POP") return;
    const marker = ((location.state ?? {}) as Record<string, unknown>).xmLead;
    const next = typeof marker === "string" && marker ? marker : null;
    if (next === active) return;
    pushedFor.current = next;
    dispatch(setCurrentLeadId(next ? Number(next) : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navType]);

  const close = () => {
    const marker = ((location.state ?? {}) as Record<string, unknown>).xmLead;
    pushedFor.current = null;
    dispatch(setCurrentLeadId(null));
    if (marker === active) navigate(-1);
  };

  if (!mounted) return null;
  return <ViewLead leadId={mounted} open={!!active} onClose={close} defaultChildId={defaultChildId} />;
}
